import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import KycBanner from "../../../components/Investor/KycBanner";
import InvestorFilterPills from "../../../components/Investor/InvestorFilterPills";
import InvestorListPagination from "../../../components/Investor/InvestorListPagination";
import MarketplaceProductCard from "../../../components/Investor/MarketplaceProductCard";
import MarketplaceOrderModal from "../../../components/Investor/MarketplaceOrderModal";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { useInvestorGate } from "../../../hooks/useInvestorGate";
import { useInvestorSession } from "../../../hooks/useInvestorSession";
import {
  useListMarketplaceCategoriesQuery,
  useListMarketplaceProductsQuery,
} from "../../../store/investor/investorMarketplaceApi";
import {
  InvestorApiErrorState,
  InvestorEmptyState,
} from "../../../components/Investor/InvestorPlatformState";

const PRODUCTS_PER_PAGE = 9;
const ALL_FILTER = "All";

export default function InvestorMarketplacePage() {
  const { ensureOrderAccess, kycStatus } = useInvestorGate();
  const { profile } = useInvestorSession();

  const [filter, setFilter] = useState(ALL_FILTER);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [orderProduct, setOrderProduct] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const { data: categoriesData } = useListMarketplaceCategoriesQuery({ per_page: 100 });

  const categoryOptions = useMemo(() => {
    const names = (categoriesData?.items || []).map((c) => c.name).filter(Boolean);
    return [ALL_FILTER, ...names];
  }, [categoriesData?.items]);

  const selectedCategory = useMemo(() => {
    if (filter === ALL_FILTER) return null;
    return (categoriesData?.items || []).find((c) => c.name === filter) || null;
  }, [filter, categoriesData?.items]);

  const apiParams = useMemo(() => {
    const params = { page, per_page: PRODUCTS_PER_PAGE };
    if (search) params.search = search;
    if (selectedCategory?.id) params.category_id = selectedCategory.id;
    else if (selectedCategory?.slug) params.category = selectedCategory.slug;
    return params;
  }, [filter, page, search, selectedCategory]);

  const { data: apiData, isFetching, isError, refetch } = useListMarketplaceProductsQuery(apiParams);

  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  const pageItems = apiData?.items || [];
  const total = apiData?.pagination?.total || pageItems.length;
  const totalPages = apiData?.pagination?.last_page || 1;
  const safePage = Math.min(page, totalPages);
  const hasFilter = filter !== ALL_FILTER || Boolean(search);

  const openOrder = (product) => {
    const detailPath = `/invest/marketplace/${product.slug || product.id}`;
    if (!ensureOrderAccess(detailPath, { intent: "order", itemId: product.slug || product.id })) {
      toast("Complete KYC to place an order");
      return;
    }
    setOrderProduct(product);
    setShowOrderModal(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  let listContent = null;
  if (isFetching) {
    listContent = <TableSkeletonLoader rows={3} columns={3} />;
  } else if (isError) {
    listContent = (
      <InvestorApiErrorState
        message="We couldn’t load marketplace products. Please try again."
        onRetry={() => refetch()}
      />
    );
  } else if (pageItems.length === 0) {
    listContent = hasFilter ? (
      <InvestorEmptyState
        title="No products match this filter"
        message="Try another category or clear your search to see available products."
      />
    ) : (
      <InvestorEmptyState
        title="No products available"
        message="Marketplace products will appear here once they are published."
      />
    );
  } else {
    listContent = (
      <div className="row g-3">
        {pageItems.map((product) => (
          <div key={product.id || product.slug} className="col-12 col-md-6 col-xl-4">
            <MarketplaceProductCard product={product} onOrder={() => openOrder(product)} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <InvestorPageShell
      title="Marketplace"
      subtitle="Browse products published by your cooperative."
      status={kycStatus !== "approved" ? kycStatus : null}
      statusLabel={kycStatus !== "approved" ? "KYC required" : null}
    >
      <KycBanner status={kycStatus} redirectTo="/invest/marketplace" intent="order" />

      <form onSubmit={handleSearchSubmit} className="mb-3 mb-md-4">
        <div className="input-group">
          <input
            type="search"
            className="form-control py-2"
            placeholder="Search products..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search marketplace products"
          />
          <button type="submit" className="btn btn-outline-secondary px-3">
            Search
          </button>
        </div>
      </form>

      <InvestorFilterPills options={categoryOptions} value={filter} onChange={setFilter} />

      <div className="mt-1" data-tour="invest-market-grid">
      {listContent}

      {pageItems.length > 0 ? (
        <InvestorListPagination
          currentPage={safePage}
          perPage={PRODUCTS_PER_PAGE}
          total={total}
          totalPages={totalPages}
          count={pageItems.length}
          onPageChange={setPage}
        />
      ) : null}
      </div>

      <MarketplaceOrderModal
        show={showOrderModal}
        product={orderProduct}
        profile={profile}
        onClose={() => {
          setShowOrderModal(false);
          setOrderProduct(null);
        }}
        onSuccess={() => {
          setShowOrderModal(false);
          setOrderProduct(null);
        }}
      />
    </InvestorPageShell>
  );
}
